/**
 * Sandbox View — non-destructive workspace for editing an attorney's entries.
 *
 * Copy of the internal EntriesView, rewired to a sandbox prof_id. Edits,
 * combines, duplicates etc. operate on a backend-provisioned shadow prof
 * (`POST /sandbox/provision`) instead of the real attorney. Two sandbox-
 * specific actions live in the top bar: Import (clone real entries into the
 * sandbox) and Reset (re-clone a single day). Sandbox edits never affect the
 * real attorney; any learnings get applied to the real attorney manually via
 * the regular Entries page.
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import {
	ChevronsUpDown,
	Check,
	User,
	Download,
	RotateCcw,
	FlaskConical,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/shared/components/ui/command";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { useAuth } from "@workos-inc/authkit-react";
import { HeaderBar } from "@/features/attorney/components/HeaderBar";
import { useDateParams } from "@/shared/hooks/useDateParams";
import { useApplicationFilter } from "@/features/attorney/hooks/useApplicationFilter";
import {
	useReportsCache,
	useMattersCache,
	useTasksCache,
	useActivitiesCache,
	useAbbreviationsCache,
	type ReportingEntry,
} from "@/shared/cache";
import type { ReportingEntryStatus } from "@/shared/cache/slices/reportingEntries";
import { StatusFilterChips } from "../components/StatusFilterChips";
import { useReportingApi } from "@/features/attorney/hooks/useReportingApi";
import { ApiError } from "@/v2/hooks/actions/types";
import { useProfessionals } from "@/features/admin/hooks/useAdminData";
import { type Professional } from "@/features/admin/utils/adminHelpers";
import { toCreateBody } from "@/v2/hooks/actions/add";
import { toUpdateBody } from "@/v2/hooks/actions/edit";
import { formatEntryId } from "@/v2/hooks/actions/entryId";
import { usePersistedState } from "@/features/attorney/hooks/usePersistedState";
import { toast } from "sonner";
import { TOAST_DURATION } from "@/shared/utils/toast";
import { showLegacyRouteStub } from "@/shared/utils/legacyRouteStubs";
import { logger } from "@/shared/utils/logger";
import { type SelectedRowData } from "@/features/attorney/components/v2/EditEntryBody";
import { InternalEditEntry } from "../components/InternalEditEntry";
import {
	SandboxImportDialog,
	SandboxResetDialog,
} from "../components/SandboxActionDialogs";
import { MergeDialog } from "@/v2/components/MergeDialog";
import { GroupDialog } from "@/v2/components/GroupDialog";
import {
	CollapseDialog,
	type CollapseDialogSubmit,
} from "@/v2/components/CollapseDialog";
import type { ActionFormSubmit } from "@/v2/components/action-dialog/ActionForm";
import type { Entry as SharedEntry } from "@/shared/types/entries";
import { InternalAddEntryDialog } from "../components/InternalAddEntryDialog";
import {
	InternalEntriesTable,
	type InternalEntry as Entry,
	type SortingMode,
	type SortDirection,
} from "../components/InternalEntriesTable";
import { useSandboxProvision } from "../hooks/useSandboxProvision";
import {
	useInternalEntries,
	useInvalidateInternalEntries,
} from "../hooks/useInternalEntries";

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const ALL_STATUSES: ReportingEntryStatus[] = ["tracked", "exported", "deleted"];

function parseStatuses(raw: string | null): Set<ReportingEntryStatus> {
	const parsed = new Set<ReportingEntryStatus>(
		raw
			?.split(",")
			.filter((s): s is ReportingEntryStatus =>
				ALL_STATUSES.includes(s as ReportingEntryStatus),
			) ?? [],
	);
	return parsed.size > 0 ? parsed : new Set(["tracked"]);
}

function formatDateForApi(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

/** Format ISO date to MM/dd/yyyy (same as EntriesContext.transformData) */
function formatDateForEntry(isoDate: string): string {
	const datePart = isoDate.split("T")[0];
	const [year, month, day] = datePart.split("-").map(Number);
	const m = String(month).padStart(2, "0");
	const d = String(day).padStart(2, "0");
	return `${m}/${d}/${year}`;
}

function toEntry(tx: ReportingEntry, status: ReportingEntryStatus): Entry {
	return {
		id: tx.id,
		date: formatDateForEntry(tx.date),
		matter: tx.matter_number || "",
		matter_id: tx.matter_id || undefined,
		matter_name: tx.matter_description || undefined,
		description: tx.description || "",
		task: tx.task || "",
		activity: tx.activity || undefined,
		document_name: tx.document_name || undefined,
		duration: tx.units,
		start_time: tx.start_time ?? undefined,
		end_time: tx.end_time || undefined,
		application: tx.application || undefined,
		status,
	};
}

function AttorneySelector({
	professionals,
	selectedProfId,
	onSelect,
}: {
	professionals: Professional[];
	selectedProfId: string | null;
	onSelect: (profId: string) => void;
}) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");

	const selectedMember = professionals.find(
		(p) => p.prof_id === selectedProfId,
	);

	const filtered = useMemo(() => {
		if (!search) return professionals;
		const term = search.toLowerCase();
		return professionals.filter(
			(p) =>
				p.full_name.toLowerCase().includes(term) ||
				(p.email && p.email.toLowerCase().includes(term)),
		);
	}, [professionals, search]);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-muted transition-colors text-left">
					<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-foreground text-sm font-medium">
						{selectedMember ? (
							selectedMember.full_name
								.split(/[\s,]+/)
								.filter(Boolean)
								.slice(0, 2)
								.map((n) => n[0])
								.join("")
								.toUpperCase()
						) : (
							<User className="h-4 w-4 text-muted-foreground" />
						)}
					</div>
					<div className="flex flex-col min-w-0">
						<span className="text-sm font-medium text-foreground truncate">
							{selectedMember ? selectedMember.full_name : "Select attorney"}
						</span>
						{!selectedMember && (
							<span className="text-xs text-muted-foreground">
								Click to choose
							</span>
						)}
					</div>
					<ChevronsUpDown className="ml-1 h-4 w-4 text-muted-foreground shrink-0" />
				</button>
			</PopoverTrigger>
			<PopoverContent className="w-[300px] p-0" align="start">
				<Command>
					<CommandInput
						placeholder="Search by name or email..."
						value={search}
						onValueChange={setSearch}
					/>
					<CommandList>
						<CommandEmpty>No attorneys found.</CommandEmpty>
						<CommandGroup>
							{filtered.map((member) => (
								<CommandItem
									key={member.prof_id}
									value={`${member.full_name} ${member.email ?? ""}`}
									onSelect={() => {
										onSelect(member.prof_id);
										setOpen(false);
										setSearch("");
									}}
								>
									<Check
										className={cn(
											"mr-2 h-4 w-4 shrink-0",
											selectedProfId === member.prof_id
												? "opacity-100"
												: "opacity-0",
										)}
									/>
									<div className="flex flex-col min-w-0">
										<span className="truncate">{member.full_name}</span>
										<span className="text-xs text-muted-foreground truncate">
											{member.email}
										</span>
									</div>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

export default function SandboxView() {
	const { user } = useAuth();
	// Source 1: firm-wide professionals (excludes @hourglass.law staff because
	// the backend's /admin/professionals endpoint hardcodes that filter and
	// ignores any param to disable it).
	const { professionals, loading: profLoading } = useProfessionals({
		activeOnly: true,
	});
	// Source 2: caller's `reports_to.manages` list — this endpoint does NOT
	// filter internal staff, so it's how Hourglass-internal users (TJ, Test
	// Account, etc.) end up visible in the picker. Anyone we want to act on
	// behalf of can be added to reports_to and will appear here.
	const { reports } = useReportsCache();

	// Merge both sources, dedupe by prof_id. Result: every firm attorney plus
	// any internal user the caller manages. Strictly scoped to this view.
	const allProfessionals = useMemo<Professional[]>(() => {
		const byId = new Map<string, Professional>();
		for (const p of professionals) byId.set(p.prof_id, p);
		for (const r of reports.manages) {
			// Shadows ride in via reports_to (provisioning inserts a manager row).
			// Backend should drop these from /user/reports; until it does, filter here.
			if (r.full_name?.startsWith("[sandbox]")) continue;
			if (!byId.has(r.prof_id)) {
				byId.set(r.prof_id, {
					prof_id: r.prof_id,
					full_name: r.full_name,
					email: r.email,
				});
			}
		}
		return Array.from(byId.values()).sort((a, b) =>
			a.full_name.localeCompare(b.full_name),
		);
	}, [professionals, reports.manages]);

	// URL-driven state. Sandbox locks the date picker to single-day only —
	// dateRange is intentionally unused, Import/Reset are date-targeted.
	const [searchParams, setSearchParams] = useSearchParams();
	const { date: selectedDate, setDate: setSelectedDate } = useDateParams();

	const visibleStatuses = useMemo(
		() => parseStatuses(searchParams.get("show")),
		[searchParams],
	);

	const toggleStatus = useCallback(
		(status: ReportingEntryStatus) => {
			const next = new URLSearchParams(window.location.search);
			const current = parseStatuses(next.get("show"));
			if (current.has(status)) current.delete(status);
			else current.add(status);
			if (current.size === 0) current.add("tracked");
			const value = ALL_STATUSES.filter((s) => current.has(s)).join(",");
			if (value === "tracked") next.delete("show");
			else next.set("show", value);
			setSearchParams(next, { replace: true });
		},
		[setSearchParams],
	);

	const selectedDateStr = useMemo(
		() => formatDateForApi(selectedDate),
		[selectedDate],
	);

	const clientFilter = searchParams.get("client") || null;
	const matterFilter = searchParams.get("matter") || null;

	const setParams = useCallback(
		(updates: Record<string, string | null>) => {
			const next = new URLSearchParams(window.location.search);
			for (const [key, value] of Object.entries(updates)) {
				if (!value) next.delete(key);
				else next.set(key, value);
			}
			setSearchParams(next, { replace: true });
		},
		[setSearchParams],
	);

	// Real attorney from URL (?attorney=PROF_ID). The dropdown shows real profs;
	// all reads/writes route to the provisioned sandbox prof_id instead.
	const realProfId = searchParams.get("attorney") || null;
	const setRealProfId = useCallback(
		(profId: string | null) => {
			const next = new URLSearchParams(window.location.search);
			if (profId) next.set("attorney", profId);
			else next.delete("attorney");
			setSearchParams(next, { replace: true });
		},
		[setSearchParams],
	);

	const {
		sandbox,
		isProvisioning,
		error: provisionError,
	} = useSandboxProvision(realProfId);
	const selectedProfId = sandbox?.sandboxProfId ?? null;

	const [sortingMode] = usePersistedState<SortingMode>(
		"sandbox:sortingMode",
		"time",
	);
	const [sortDirection] = usePersistedState<SortDirection>(
		"sandbox:sortDirection",
		"desc",
	);

	const {
		isVisible: isApplicationVisible,
		toggleApplication,
		filterEntries,
		showAll: showAllApplications,
		hideAll: hideAllApplications,
	} = useApplicationFilter(user?.email || "");

	const {
		tracked: cachedTracked,
		exported: cachedExported,
		deleted: cachedDeleted,
		isLoading: entriesLoading,
		isError: entriesError,
		error: entriesErrorMessage,
	} = useInternalEntries(selectedProfId, selectedDate, selectedDate, true);
	const invalidateInternal = useInvalidateInternalEntries();
	const refreshSandbox = useCallback(
		() => invalidateInternal(selectedProfId),
		[invalidateInternal, selectedProfId],
	);

	// Writes route to the sandbox prof via x-act-as-prof-id, so every existing
	// /records and /v2/entries endpoint operates on the shadow without changes.
	const { makeReportingRequest } = useReportingApi(selectedProfId);

	const [, setMutatingIds] = useState<Set<number>>(new Set());

	const { matters } = useMattersCache();
	const { tasks } = useTasksCache();
	const { activities } = useActivitiesCache();
	const { abbreviationProps } = useAbbreviationsCache();

	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [selectedRowData, setSelectedRowData] =
		useState<SelectedRowData | null>(null);
	const [showImportDialog, setShowImportDialog] = useState(false);
	const [showResetDialog, setShowResetDialog] = useState(false);
	const [mergeEntries, setMergeEntries] = useState<SharedEntry[] | null>(null);
	const [mergeConflicts, setMergeConflicts] = useState<
		{ field: string; values: string[] }[] | undefined
	>(undefined);
	const [groupEntries, setGroupEntries] = useState<SharedEntry[] | null>(null);
	const [groupConflicts, setGroupConflicts] = useState<
		{ field: string; values: string[] }[] | undefined
	>(undefined);
	const [collapseEntries, setCollapseEntries] = useState<SharedEntry[] | null>(
		null,
	);
	const [actionInProgress, setActionInProgress] = useState<
		"merge" | "group" | "collapse" | "delete" | "duplicate" | null
	>(null);

	const realFullName = useMemo(
		() => sandbox?.fullName.replace(/^\[sandbox\]\s*/, "") ?? "",
		[sandbox?.fullName],
	);

	const statusKey = ALL_STATUSES.filter((s) => visibleStatuses.has(s)).join(
		",",
	);
	useEffect(() => {
		setCheckedRows(new Set());
	}, [selectedProfId, statusKey]);

	const sourceEntries = useMemo(() => {
		const parts: ReportingEntry[] = [];
		if (visibleStatuses.has("tracked")) parts.push(...cachedTracked);
		if (visibleStatuses.has("exported")) parts.push(...cachedExported);
		if (visibleStatuses.has("deleted")) parts.push(...cachedDeleted);
		return parts;
	}, [visibleStatuses, cachedTracked, cachedExported, cachedDeleted]);

	// ids are unique across the three buckets — one row never appears in two.
	const statusById = useMemo(() => {
		const m = new Map<number, ReportingEntryStatus>();
		for (const e of cachedTracked) m.set(e.id, "tracked");
		for (const e of cachedExported) m.set(e.id, "exported");
		for (const e of cachedDeleted) m.set(e.id, "deleted");
		return m;
	}, [cachedTracked, cachedExported, cachedDeleted]);

	const allProfEntriesForFilters = useMemo(
		() => [...cachedTracked, ...cachedExported, ...cachedDeleted],
		[cachedTracked, cachedExported, cachedDeleted],
	);

	const availableClients = useMemo(() => {
		const set = new Set<string>();
		for (const e of allProfEntriesForFilters) if (e.client) set.add(e.client);
		return Array.from(set).sort();
	}, [allProfEntriesForFilters]);

	const availableMatters = useMemo(() => {
		const map = new Map<
			string,
			{
				matterId: string;
				matterNumber: string;
				matterDescription: string;
				client: string;
			}
		>();
		for (const e of allProfEntriesForFilters) {
			if (e.matter_id && !map.has(e.matter_id)) {
				if (!clientFilter || e.client === clientFilter) {
					map.set(e.matter_id, {
						matterId: e.matter_id,
						matterNumber: e.matter_number || "",
						matterDescription: e.matter_description || "",
						client: e.client || "Unknown",
					});
				}
			}
		}
		return Array.from(map.values()).sort((a, b) =>
			a.matterNumber.localeCompare(b.matterNumber),
		);
	}, [allProfEntriesForFilters, clientFilter]);

	// Single-day only: no dateRange branch (sandbox locks the picker).
	const dateFilteredEntries = useMemo(() => {
		let entries = sourceEntries.filter(
			(e) => e.date.split("T")[0] === selectedDateStr,
		);
		if (clientFilter)
			entries = entries.filter((e) => e.client === clientFilter);
		if (matterFilter)
			entries = entries.filter((e) => e.matter_id === matterFilter);
		return entries;
	}, [sourceEntries, selectedDateStr, clientFilter, matterFilter]);

	const tabFilteredEntries = useMemo(() => {
		const mult = sortDirection === "asc" ? 1 : -1;
		const toE = (tx: ReportingEntry) =>
			toEntry(tx, statusById.get(tx.id) ?? "tracked");
		if (sortingMode === "matter") {
			return [...dateFilteredEntries].map(toE).sort((a, b) => {
				const mA = a.matter_name || a.matter || "";
				const mB = b.matter_name || b.matter || "";
				if (!mA && mB) return 1;
				if (mA && !mB) return -1;
				if (!mA && !mB) return 0;
				return mult * mA.toLowerCase().localeCompare(mB.toLowerCase());
			});
		}
		return [...dateFilteredEntries]
			.sort(
				(a, b) => mult * (a.start_time || "").localeCompare(b.start_time || ""),
			)
			.map(toE);
	}, [dateFilteredEntries, sortingMode, sortDirection, statusById]);

	const filteredEntries = useMemo(
		() => filterEntries(tabFilteredEntries),
		[tabFilteredEntries, filterEntries],
	);

	const [checkedRows, setCheckedRows] = useState<Set<number>>(() => new Set());

	const selectionCount = checkedRows.size;
	const selectedIds = useMemo(() => Array.from(checkedRows), [checkedRows]);

	const selectionStatuses = useMemo(() => {
		const s = new Set<ReportingEntryStatus>();
		for (const id of checkedRows) {
			const st = statusById.get(id);
			if (st) s.add(st);
		}
		return s;
	}, [checkedRows, statusById]);

	const selectionAllTracked =
		selectionCount > 0 &&
		selectionStatuses.size === 1 &&
		selectionStatuses.has("tracked");
	const selectionHasDeleted = selectionStatuses.has("deleted");

	const handleExport = useCallback(() => {
		toast.error(
			"Sandbox entries can't be exported. Apply changes to the real attorney manually via the Entries page.",
		);
	}, []);

	const handleDelete = useCallback(async () => {
		if (selectedIds.length === 0) return;
		if (selectionHasDeleted) {
			toast.error("Some entries are already deleted", {
				duration: TOAST_DURATION.error,
			});
			return;
		}
		setCheckedRows(new Set());
		setActionInProgress("delete");

		showLegacyRouteStub("/records/delete", "/v2/entry-groups");
		setActionInProgress(null);
	}, [selectedIds, selectionHasDeleted]);

	const handleDuplicate = useCallback(async () => {
		if (selectedIds.length === 0) return;
		if (selectionHasDeleted) {
			toast.error("Deleted entries can't be duplicated", {
				duration: TOAST_DURATION.error,
			});
			return;
		}

		setMutatingIds((prev) => {
			const next = new Set(prev);
			selectedIds.forEach((id) => next.add(id));
			return next;
		});
		setActionInProgress("duplicate");

		setCheckedRows(new Set());
		showLegacyRouteStub("/records/duplicate", "/v2/entry-groups");
		setMutatingIds((prev) => {
			const next = new Set(prev);
			selectedIds.forEach((id) => next.delete(id));
			return next;
		});
		setActionInProgress(null);
	}, [selectedIds, selectionHasDeleted]);

	const handleMerge = useCallback(() => {
		if (selectedIds.length < 2) {
			toast.error("Select at least 2 entries to merge", {
				duration: TOAST_DURATION.error,
			});
			return;
		}
		if (!selectionAllTracked) {
			toast.error("Only tracked entries can be merged", {
				duration: TOAST_DURATION.error,
			});
			return;
		}
		const sources = filteredEntries.filter((e) => checkedRows.has(e.id));
		setMergeEntries(sources as SharedEntry[]);
		setMergeConflicts(undefined);
	}, [selectedIds, selectionAllTracked, filteredEntries, checkedRows]);

	const handleMergeSubmit = useCallback(
		async (values: ActionFormSubmit) => {
			const sources = mergeEntries ?? [];
			const ids = sources.map((e) => e.id);
			if (ids.length === 0) return;

			setActionInProgress("merge");

			try {
				const resp = await makeReportingRequest({
					endpoint: "/v2/entries/merge",
					method: "POST",
					body: {
						sourceIds: ids.map(formatEntryId),
						matterId: values.matterId,
						taskId: values.taskId,
						activityId: values.activityId,
						date: formatDateForApi(values.date),
						unitsSeconds: values.unitsSeconds,
					},
				});
				if (resp.apiError) throw new ApiError(resp.apiError);
				if (resp.error) throw new Error(String(resp.error));

				setCheckedRows(new Set());
				setMergeEntries(null);
				setMergeConflicts(undefined);
				toast.success(`${ids.length} entries merged`);
				refreshSandbox();
			} catch (e) {
				// FIELD_CONFLICT keeps the dialog open with the conflicts overlaid so
				// the user can resolve them and resubmit. Any other error closes the
				// dialog and surfaces a toast.
				if (
					e instanceof ApiError &&
					e.code === "FIELD_CONFLICT" &&
					e.conflicts?.length
				) {
					setMergeConflicts(e.conflicts);
					return;
				}
				setMergeEntries(null);
				toast.error("Failed to merge entries", {
					duration: TOAST_DURATION.error,
					description: e instanceof Error ? e.message : undefined,
				});
			} finally {
				setActionInProgress(null);
			}
		},
		[mergeEntries, makeReportingRequest, refreshSandbox],
	);

	const handleGroup = useCallback(() => {
		if (selectedIds.length < 2) {
			toast.error("Select at least 2 entries to group", {
				duration: TOAST_DURATION.error,
			});
			return;
		}
		if (!selectionAllTracked) {
			toast.error("Only tracked entries can be grouped", {
				duration: TOAST_DURATION.error,
			});
			return;
		}
		const sources = filteredEntries.filter((e) => checkedRows.has(e.id));
		setGroupEntries(sources as SharedEntry[]);
		setGroupConflicts(undefined);
	}, [selectedIds, selectionAllTracked, filteredEntries, checkedRows]);

	const handleGroupSubmit = useCallback(
		async (values: ActionFormSubmit) => {
			const sources = groupEntries ?? [];
			const ids = sources.map((e) => e.id);
			if (ids.length === 0) return;
			setActionInProgress("group");
			try {
				const resp = await makeReportingRequest({
					endpoint: "/v2/entries/group",
					method: "POST",
					body: {
						sourceIds: ids.map(formatEntryId),
						matterId: values.matterId,
						taskId: values.taskId,
						activityId: values.activityId,
						date: formatDateForApi(values.date),
						unitsSeconds: values.unitsSeconds,
						...(values.includeUnits !== undefined
							? { includeUnits: values.includeUnits }
							: {}),
					},
				});
				if (resp.apiError) throw new ApiError(resp.apiError);
				if (resp.error) throw new Error(String(resp.error));
				setCheckedRows(new Set());
				setGroupEntries(null);
				setGroupConflicts(undefined);
				toast.success(`${ids.length} entries grouped`);
				refreshSandbox();
			} catch (e) {
				if (
					e instanceof ApiError &&
					e.code === "FIELD_CONFLICT" &&
					e.conflicts?.length
				) {
					setGroupConflicts(e.conflicts);
					return;
				}
				setGroupEntries(null);
				toast.error("Failed to group entries", {
					duration: TOAST_DURATION.error,
					description: e instanceof Error ? e.message : undefined,
				});
			} finally {
				setActionInProgress(null);
			}
		},
		[groupEntries, makeReportingRequest, refreshSandbox],
	);

	const handleCollapse = useCallback(() => {
		if (selectedIds.length < 2) {
			toast.error("Select at least 2 entries to collapse", {
				duration: TOAST_DURATION.error,
			});
			return;
		}
		if (!selectionAllTracked) {
			toast.error("Only tracked entries can be collapsed", {
				duration: TOAST_DURATION.error,
			});
			return;
		}
		const sources = filteredEntries.filter((e) => checkedRows.has(e.id));
		setCollapseEntries(sources as SharedEntry[]);
	}, [selectedIds, selectionAllTracked, filteredEntries, checkedRows]);

	const handleCollapseSubmit = useCallback(
		async (values: CollapseDialogSubmit) => {
			const sources = collapseEntries ?? [];
			const ids = sources.map((e) => e.id);
			if (ids.length === 0) return;
			setActionInProgress("collapse");
			try {
				const resp = await makeReportingRequest({
					endpoint: "/v2/entries/collapse",
					method: "POST",
					body: {
						sourceIds: ids.map(formatEntryId),
						mainSourceId: formatEntryId(values.mainSourceId),
						matterId: values.matterId,
						taskId: values.taskId,
						activityId: values.activityId,
						date: formatDateForApi(values.date),
						unitsSeconds: values.unitsSeconds,
					},
				});
				if (resp.apiError) throw new ApiError(resp.apiError);
				if (resp.error) throw new Error(String(resp.error));
				setCheckedRows(new Set());
				setCollapseEntries(null);
				toast.success(`${ids.length} entries collapsed`);
				refreshSandbox();
			} catch (e) {
				setCollapseEntries(null);
				toast.error("Failed to collapse entries", {
					duration: TOAST_DURATION.error,
					description: e instanceof Error ? e.message : undefined,
				});
			} finally {
				setActionInProgress(null);
			}
		},
		[collapseEntries, makeReportingRequest, refreshSandbox],
	);

	const handleAddEntry = useCallback(() => {
		if (!selectedProfId) {
			toast.error("Select an attorney before adding an entry");
			return;
		}
		setAddDialogOpen(true);
	}, [selectedProfId]);

	const handleSaveNewEntry = useCallback(
		async (
			newEntry: {
				id: number;
				date: string;
				matter: string;
				matter_id: string;
				description: string;
				duration: number;
				task: string;
				activity: string;
			},
			_shouldExport: boolean,
		) => {
			try {
				const resp = await makeReportingRequest({
					endpoint: "/v2/entries",
					method: "POST",
					body: toCreateBody(newEntry),
				});
				if (resp.error) throw new Error(String(resp.error));
				toast.success("Entry added");
				refreshSandbox();
			} catch (e) {
				if (e instanceof Error)
					logger.captureException(e, { action: "sandboxAddEntry" });
				toast.error("Failed to add entry", {
					duration: TOAST_DURATION.error,
					description: e instanceof Error ? e.message : undefined,
				});
			}
		},
		[makeReportingRequest, refreshSandbox],
	);

	const handleSaveEditEntry = useCallback(
		async (
			updatedEntry: {
				id: number;
				date: string;
				matter: string;
				matter_id: string;
				description: string;
				duration: number;
				task: string;
				activity: string;
			},
			_shouldExport: boolean,
		) => {
			setMutatingIds((prev) => {
				const next = new Set(prev);
				next.add(updatedEntry.id);
				return next;
			});
			setShowEditModal(false);

			try {
				const resp = await makeReportingRequest({
					endpoint: `/v2/entries/${formatEntryId(updatedEntry.id)}`,
					method: "PATCH",
					body: toUpdateBody(updatedEntry),
				});
				if (resp.error) throw new Error(String(resp.error));
				toast.success("Entry saved");
				refreshSandbox();
			} catch (e) {
				refreshSandbox();
				if (e instanceof Error)
					logger.captureException(e, {
						action: "sandboxEditEntry",
						entryId: updatedEntry.id,
					});
				toast.error("Failed to save entry", {
					duration: TOAST_DURATION.error,
					description: e instanceof Error ? e.message : undefined,
				});
			} finally {
				setMutatingIds((prev) => {
					const next = new Set(prev);
					next.delete(updatedEntry.id);
					return next;
				});
			}
		},
		[makeReportingRequest, refreshSandbox],
	);

	const handleEditDuplicate = useCallback(
		async (
			_entry: {
				date: string;
				matter: string;
				matter_id: string;
				description: string;
				duration: number;
				task: string;
				activity: string;
				document_name: string | null;
				client: string | null;
				units_seconds: number;
				start_time: string | null;
				end_time: string | null;
			},
			originalId: number,
		) => {
			setMutatingIds((prev) => {
				const next = new Set(prev);
				next.add(originalId);
				return next;
			});
			showLegacyRouteStub("/records/duplicate", "/v2/entry-groups");
			setMutatingIds((prev) => {
				const next = new Set(prev);
				next.delete(originalId);
				return next;
			});
		},
		[],
	);

	const handleResolveFeedback = useCallback(
		async (entryId: number) => {
			showLegacyRouteStub(
				`/records/${entryId}/resolve-feedback`,
				"/v2/entry-groups/{entryGroupId}",
			);
		},
		[],
	);

	const handleRowClick = (entry: Entry) => {
		if (entry.status !== "tracked") return;
		const cached = sourceEntries.find((e) => e.id === entry.id);
		setSelectedRowData({
			id: entry.id,
			date: entry.date,
			matter_name: entry.matter,
			matter_id: entry.matter_id || "",
			description: entry.description,
			duration: entry.duration,
			task: entry.task,
			activity: entry.activity || "",
			document_name: entry.document_name || "",
			original_description: cached?.original_description ?? null,
			original_units: cached?.original_units ?? null,
			original_matter: cached?.original_matter ?? null,
			original_task: cached?.original_task ?? null,
			original_activity: cached?.original_activity ?? null,
			attorney_feedback: cached?.attorney_feedback ?? null,
		});
		setShowEditModal(true);
	};

	const emptyMessage = useMemo(() => {
		if (!selectedProfId)
			return "Select an attorney to view their time entries.";
		if (filteredEntries.length > 0) return undefined;
		if (entriesError)
			return `Failed to load entries${entriesErrorMessage ? `: ${entriesErrorMessage}` : "."}`;
		if (tabFilteredEntries.length > 0)
			return "No entries match the current filter.";
		return "No entries for this date.";
	}, [
		selectedProfId,
		filteredEntries.length,
		tabFilteredEntries.length,
		entriesError,
		entriesErrorMessage,
	]);

	if (profLoading) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-muted-foreground">Loading attorneys...</p>
			</div>
		);
	}

	const headerViewMode = visibleStatuses.has("tracked")
		? ("tracked" as const)
		: ("history" as const);

	return (
		<div className="flex flex-col h-full bg-orange-50/40 dark:bg-orange-950/10">
			<div className="flex items-center gap-2 px-5 py-2 bg-orange-200/60 border-b border-orange-300 text-orange-900 dark:bg-orange-900/40 dark:border-orange-800 dark:text-orange-100">
				<FlaskConical className="size-4 shrink-0" />
				<span className="text-xs font-semibold uppercase tracking-wide">
					Sandbox
				</span>
				<span className="text-xs text-orange-900/80 dark:text-orange-100/80">
					{sandbox
						? `Changes never affect ${realFullName}'s real entries.`
						: isProvisioning
							? "Provisioning sandbox..."
							: provisionError
								? `Provisioning error: ${provisionError}`
								: "Select an attorney to begin."}
				</span>
			</div>

			<div className="flex items-center gap-3 px-5 py-3 border-b border-border">
				<AttorneySelector
					professionals={allProfessionals}
					selectedProfId={realProfId}
					onSelect={setRealProfId}
				/>

				<div className="ml-auto flex items-center gap-2">
					{(
						[
							{
								label: "Import",
								Icon: Download,
								onClick: () => setShowImportDialog(true),
							},
							{
								label: "Reset",
								Icon: RotateCcw,
								onClick: () => setShowResetDialog(true),
							},
						] as const
					).map(({ label, Icon, onClick }) => (
						<Button
							key={label}
							type="button"
							variant="outline"
							size="sm"
							onClick={onClick}
							disabled={!selectedProfId}
							className="h-8 gap-1.5 text-xs"
						>
							<Icon className="size-3.5" />
							{label}
						</Button>
					))}
					<StatusFilterChips active={visibleStatuses} onToggle={toggleStatus} />
				</div>
			</div>

			<div className="px-3">
				<HeaderBar
					viewMode={headerViewMode}
					date={selectedDate}
					onDateChange={setSelectedDate}
					showTimer={false}
					timerRunning={false}
					elapsedSeconds={0}
					onStart={() => {}}
					onStop={() => {}}
					onReset={() => {}}
					selectionCount={selectionCount}
					onAddEntry={handleAddEntry}
					onMerge={handleMerge}
					onGroup={handleGroup}
					onCollapse={handleCollapse}
					onDuplicate={handleDuplicate}
					onDelete={handleDelete}
					onExport={handleExport}
					isExporting={false}
					actionInProgress={actionInProgress}
					isApplicationVisible={isApplicationVisible}
					onToggleApplication={toggleApplication}
					onShowAllApplications={showAllApplications}
					onHideAllApplications={hideAllApplications}
					clientMatter={{
						clients: availableClients,
						matters: availableMatters,
						clientFilter,
						matterFilter,
						onClientChange: (client) => setParams({ client, matter: null }),
						onMatterChange: (matterId, client) =>
							matterId === null
								? setParams({ matter: null })
								: setParams({ matter: matterId, client }),
					}}
				/>
			</div>

			<div className="flex-1 flex flex-col overflow-hidden">
				<div className="border border-border rounded-lg overflow-hidden flex-1 flex flex-col mx-3 mb-3">
					{entriesLoading ? (
						<div className="flex-1 flex items-center justify-center text-muted-foreground">
							Loading...
						</div>
					) : (
						<InternalEntriesTable
							key={selectedDate.toDateString()}
							entries={filteredEntries}
							matters={matters}
							checkedRows={checkedRows}
							setCheckedRows={setCheckedRows}
							isLoading={entriesLoading}
							onRowClick={selectedProfId ? handleRowClick : undefined}
							sortingMode={sortingMode}
							sortDirection={sortDirection}
							emptyMessage={emptyMessage}
						/>
					)}
				</div>
			</div>

			<InternalAddEntryDialog
				open={addDialogOpen}
				onOpenChange={setAddDialogOpen}
				onSave={handleSaveNewEntry}
				matters={matters}
				tasks={tasks}
				activities={activities}
				currentDate={selectedDate}
				abbreviations={abbreviationProps}
			/>

			{selectedRowData && selectedProfId && (
				<InternalEditEntry
					onSave={handleSaveEditEntry}
					onDuplicate={handleEditDuplicate}
					onResolveFeedback={() => handleResolveFeedback(selectedRowData.id)}
					attorneyFeedback={selectedRowData.attorney_feedback ?? null}
					matters={matters}
					tasks={tasks}
					activities={activities}
					selectedRowData={selectedRowData}
					open={showEditModal}
					onOpenChange={setShowEditModal}
					abbreviations={abbreviationProps}
				/>
			)}

			<MergeDialog
				entries={mergeEntries ?? []}
				open={mergeEntries !== null}
				onOpenChange={(open) => {
					if (!open) {
						setMergeEntries(null);
						setMergeConflicts(undefined);
					}
				}}
				conflicts={mergeConflicts}
				onSubmit={handleMergeSubmit}
			/>

			<GroupDialog
				entries={groupEntries ?? []}
				open={groupEntries !== null}
				onOpenChange={(open) => {
					if (!open) {
						setGroupEntries(null);
						setGroupConflicts(undefined);
					}
				}}
				conflicts={groupConflicts}
				onSubmit={handleGroupSubmit}
			/>

			<CollapseDialog
				entries={collapseEntries ?? []}
				open={collapseEntries !== null}
				onOpenChange={(open) => {
					if (!open) setCollapseEntries(null);
				}}
				onSubmit={handleCollapseSubmit}
			/>

			{selectedProfId && sandbox && (
				<>
					<SandboxImportDialog
						sandboxProfId={selectedProfId}
						realFullName={realFullName}
						date={selectedDate}
						open={showImportDialog}
						onOpenChange={setShowImportDialog}
					/>
					<SandboxResetDialog
						sandboxProfId={selectedProfId}
						realFullName={realFullName}
						date={selectedDate}
						open={showResetDialog}
						onOpenChange={setShowResetDialog}
					/>
				</>
			)}
		</div>
	);
}
